import { useTranslation } from "react-i18next";
import { FileSpreadsheet, User, UsersRound } from "lucide-react";
import { cn } from "../../lib/utils.js";
import type { CampaignManageSourceTab } from "./campaigns-presentation.js";

const TABS: Array<{
  id: CampaignManageSourceTab;
  icon: typeof User;
  countKey: "contacts" | "groups" | "csv";
}> = [
  { id: "contacts", icon: User, countKey: "contacts" },
  { id: "groups", icon: UsersRound, countKey: "groups" },
  { id: "csv", icon: FileSpreadsheet, countKey: "csv" },
];

export function CampaignAudienceSourceTabs({
  active,
  counts,
  disabled,
  onSelect,
}: {
  active: CampaignManageSourceTab;
  counts: { contacts: number; groups: number; csv: number };
  disabled?: boolean;
  onSelect: (tab: CampaignManageSourceTab) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-w-0 gap-1 overflow-x-auto overscroll-x-contain px-6"
      role="tablist"
      aria-label={t("campaigns.manage.sources")}
    >
      {TABS.map((tab) => {
        const selected = tab.id === active;
        const Icon = tab.icon;
        const count = counts[tab.countKey];
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            data-testid={`campaign-source-tab-${tab.id}`}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[0.8125rem] font-medium",
              selected
                ? "bg-accent/12 text-accent"
                : "text-muted hover:bg-surface-muted hover:text-foreground",
              disabled && "opacity-60",
            )}
            onClick={() => onSelect(tab.id)}
          >
            <Icon size={14} aria-hidden="true" />
            <span>{t(`campaigns.sources.${tab.id}`)}</span>
            <span className="tabular-nums text-muted">({count})</span>
          </button>
        );
      })}
    </div>
  );
}
